import { Component, Input, OnInit } from '@angular/core';
import { HistInfo } from '../../../core/gb-api';
import { TimepointService } from '../../../core/services/timepoint.service';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { HistoryDirective } from '../../directives/history.directive';
import { RouterLink } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'cap-link',
  templateUrl: './link.component.html',
  styleUrls: ['./link.component.scss'],
  imports: [HistoryDirective, RouterLink, NgbTooltip, NgTemplateOutlet, TranslatePipe]
})
export class LinkComponent implements OnInit {
  constructor(private timepointService: TimepointService) {
    keysToTranslate<string>(['DetailsZumGeschaeftAnzeigen', 'AnzeigeBauprojekt', 'detailsGrundstueck', 'detailsGemeindeGb']);
  }

  @Input()
  target: NavTarget;
  @Input()
  params: Array<string>;

  @Input()
  set queryParams(value: { [k: string]: any }) {
    this._queryParams = value;
    this.updateEnrichedQueryParams();
  }

  get queryParams(): { [k: string]: any } {
    return this._queryParams;
  }

  private _queryParams: { [k: string]: any };
  @Input()
  tooltipOverride: string;
  @Input()
  tooltipParams: any;
  @Input()
  histinfo: HistInfo;
  @Input()
  placement = 'top';
  @Input()
  enableNavigation = true;
  @Input()
  openInNewTab = false;

  enrichedQueryParams: { [k: string]: any };
  path: string;
  tooltip: string;

  public static getPath(target: NavTarget): string {
    const found = targets.find((a) => a.target === target);
    if (found) {
      return found.path;
    }
    return null;
  }

  private static convert(params: Array<string>) {
    if (!params) {
      return '';
    }
    if (params.length === 1) {
      return params[0];
    }
    return params.join('/');
  }

  ngOnInit() {
    this.updateEnrichedQueryParams();
    const linkElement = this.getEntry(this.target);
    this.path = linkElement.path + LinkComponent.convert(this.params);
    this.tooltip = this.tooltipOverride ? this.tooltipOverride : linkElement.tooltip;
  }

  private updateEnrichedQueryParams() {
    if (this.timepointService?.timepointCtx?.timepointId) {
      if (this._queryParams) {
        this.enrichedQueryParams = {
          ...this._queryParams,
          timepointId: this.timepointService.timepointCtx.timepointId
        };
      } else {
        this.enrichedQueryParams = { timepointId: this.timepointService.timepointCtx.timepointId };
      }
    } else {
      this.enrichedQueryParams = this._queryParams;
    }
  }

  private getEntry(target: NavTarget): Entry {
    const entries = targets.filter((e) => e.target === target);
    if (entries.length !== 1) {
      throw new Error('No navigation target found.');
    }
    return entries[0];
  }
}

export interface LinkTargetWithParamsAndTooltip {
  target: NavTarget;
  params: Array<string>;
  tooltipParams: { [k: string]: any };
}

interface Entry {
  target: NavTarget;
  path: string;
  tooltip: string;
}

export enum NavTarget {
  NAV_GRUNDSTUECK,
  NAV_GRUNDSTUECK_DIENSTBARKEITEN,
  NAV_GRUNDSTUECK_RANGREGELUNGEN,
  NAV_GRUNDSTUECK_EIGENTUM,
  NAV_GRUNDSTUECK_PFANDRECHTE,
  NAV_GRUNDSTUECK_GRUNDLASTEN,
  NAV_GRUNDSTUECK_VORMERKUNGEN,
  NAV_GRUNDSTUECK_ANMERKUNGEN,
  NAV_GRUNDSTUECK_OEREB,
  NAV_GRUNDSTUECK_NESKOAB,
  NAV_PERSON_EIGENTUEMER_VERHAELTNISSE,
  NAV_PERSON_GLAEUBIGERAUSZUG,
  NAV_PERSON_UEBRIGEBERECHTIGTE,
  NAV_PERSON_STAMM,
  NAV_PERSON_GB,
  NAV_PERSON_GB_VERGLEICH,
  NAV_PERSON_ZPV,
  NAV_RECHT,
  NAV_GRUNDBUCHGESCHAEFT,
  NAV_LOESCHGESCHAEFT,
  NAV_GEOMETERGESCHAEFT,
  NAV_GRUNDBUCHMELDUNG,
  NAV_GEBAEUDE,
  NAV_GWR_GEBAUEDE,
  NAV_GWR_WOHNUNG,
  NAV_GWR_BAUPROJEKT,
  NAV_ECH0134_MELDUNGSEMPFAENGER,
  NAV_NESKOAB_GRUNDSTUECK,
  NAV_GEMEINDE_GB,
  NAV_GRUNDSTUECK_ES,
  NAV_STAPELVERARBEITUNG
}

export const targets: Array<Entry> = [
  {
    target: NavTarget.NAV_GRUNDSTUECK,
    path: '/grundstueck/detail/uebersicht/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_ES,
    path: '/grundstueck/es/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_DIENSTBARKEITEN,
    path: '/grundstueck/detail/dienstbarkeiten/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_RANGREGELUNGEN,
    path: '/grundstueck/detail/rangregelungen/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_EIGENTUM,
    path: '/grundstueck/detail/eigentum/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_PFANDRECHTE,
    path: '/grundstueck/detail/pfandrechte/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_GRUNDLASTEN,
    path: '/grundstueck/detail/grundlasten/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_VORMERKUNGEN,
    path: '/grundstueck/detail/vormerkungen/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_ANMERKUNGEN,
    path: '/grundstueck/detail/vormerkungen/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_OEREB,
    path: '/grundstueck/detail/oereb/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GRUNDSTUECK_NESKOAB,
    path: '/grundstueck/detail/neskoab/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_PERSON_EIGENTUEMER_VERHAELTNISSE,
    path: '/grundbuch/person/eigentuemer/verhaeltnisse/',
    tooltip: 'tooltip.EIGENTUEMER'
  },
  {
    target: NavTarget.NAV_PERSON_GLAEUBIGERAUSZUG,
    path: '/grundbuch/person/glaeubiger/auszug/',
    tooltip: 'tooltip.GLAEUBIGER_SCHULDNER'
  },
  {
    target: NavTarget.NAV_PERSON_UEBRIGEBERECHTIGTE,
    path: '/grundbuch/person/uebrigeberechtigte/auszug/',
    tooltip: 'tooltip.UEBRIGE_BERECHTIGTE'
  },
  {
    target: NavTarget.NAV_PERSON_STAMM,
    path: '/grundbuch/person/detail/',
    tooltip: 'tooltip.detailsPersonStamm'
  },
  {
    target: NavTarget.NAV_PERSON_GB,
    path: '/grundbuch/persongb/detail/',
    tooltip: 'tooltip.detailsPersonGb'
  },
  {
    target: NavTarget.NAV_PERSON_GB_VERGLEICH,
    path: '/grundbuch/persongb/vergleich/',
    tooltip: null
  },
  {
    target: NavTarget.NAV_PERSON_ZPV,
    path: '/stammdaten/zentrale-personen/detail/',
    tooltip: 'tooltip.detailsPersonZpv'
  },
  {
    target: NavTarget.NAV_RECHT,
    path: '/grundbuch/recht/detail/',
    tooltip: 'DetailsZuArtAnzeigen'
  },
  {
    target: NavTarget.NAV_GRUNDBUCHGESCHAEFT,
    path: '/grundbuch/grundbuchgeschaeft/detail/',
    tooltip: 'DetailsZumGeschaeftAnzeigen'
  },
  {
    target: NavTarget.NAV_LOESCHGESCHAEFT,
    path: '/grundbuch/grundbuchgeschaeft/detail/',
    tooltip: 'DetailsZumLöschgeschaeftAnzeigen'
  },
  {
    target: NavTarget.NAV_GEOMETERGESCHAEFT,
    path: '/vermessung/geometergeschaeft/detail/',
    tooltip: 'detailsGeometerGeschäft'
  },
  {
    target: NavTarget.NAV_GRUNDBUCHMELDUNG,
    path: '/grundbuch/grundbuchmeldung/detail/',
    tooltip: 'grundbuchmeldung.tooltip'
  },
  {
    target: NavTarget.NAV_GEBAEUDE,
    path: '/vermessung/gebaeude/detail/',
    tooltip: 'AnzeigeGebäude'
  },
  {
    target: NavTarget.NAV_GWR_GEBAUEDE,
    path: '/gwr/gebaeude/detail/',
    tooltip: 'AnzeigeGebäude'
  },
  {
    target: NavTarget.NAV_GWR_WOHNUNG,
    path: '/gwr/gebaeude/detail/',
    tooltip: 'AnzeigeWohnung'
  },
  {
    target: NavTarget.NAV_GWR_BAUPROJEKT,
    path: '/gwr/bauprojekt/detail/',
    tooltip: 'AnzeigeBauprojekt'
  },
  {
    target: NavTarget.NAV_ECH0134_MELDUNGSEMPFAENGER,
    path: '/system/ech0134/meldungsempfaenger/detail/',
    tooltip: 'tooltip.meldungsempfaenger'
  },
  {
    target: NavTarget.NAV_NESKOAB_GRUNDSTUECK,
    path: '/neskoab/detail/',
    tooltip: 'detailsGrundstueck'
  },
  {
    target: NavTarget.NAV_GEMEINDE_GB,
    path: '/stammdaten/gemeinde/gb/detail/',
    tooltip: 'detailsGemeindeGb'
  },
  {
    target: NavTarget.NAV_STAPELVERARBEITUNG,
    path: '/system/import-export/detail/',
    tooltip: 'tooltip.detailsAuftrag'
  }
];
